import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { RouteComponentProps } from 'react-router';
import { Button, ButtonGroup } from '@teamsnap/teamsnap-ui';
import { isOneOf, Role } from 'auth';
import { AuthContext } from 'auth/dist/contexts';
import { TEAMSNAP_URL } from 'core/constants';
import { ORG_DEPOSIT_ACCOUNTS_FINANCIALS_URL } from 'core/utils';

import SeasonMerchantAccount from 'components/season/SeasonMerchantAccount/SeasonMerchantAccount';
import CreateAccountDrawer from 'components/form/create-account-drawer';

import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import * as ShowSeason from 'core/gql/queries/show-season';
import * as ShowOrganization from 'core/gql/queries/show-organizations';
import * as SetSeasonPaymentAccount from 'core/gql/mutations/set-season-payment-account';
import * as CreateOnboardingLink from 'core/gql/mutations/create-onboarding-link';
import * as GqlForms from '../../core/gql/queries/get-all-forms-reg';

type RouteParams = { orgId: string; programId: string; seasonId: string };

export type RegType = 'reg1' | 'reg2' | 'both';

const SeasonFinancialsPage: React.FC<RouteComponentProps<RouteParams>> = ({
  match: {
    params: { orgId, programId, seasonId },
  },
}) => {
  const context = React.useContext(AuthContext);

  const [showDrawer, setShowDrawer] = React.useState(false);
  const [currentDepositAccount, setCurrentDepositAccount] = React.useState(null);
  const [redirecting, setRedirecting] = React.useState(false);
  const [regType, setRegType] = React.useState<RegType>(null);

  const { data: paymentsAccounts } = useQuery(ListPaymentAccounts.QUERY, {
    variables: { scopeId: +orgId, scopeType: 'organization' },
    errorPolicy: 'all',
  });

  const getSeasonData = useQuery(ShowSeason.QUERY, {
    variables: { seasonId: +seasonId },
    skip: !seasonId,
    errorPolicy: 'all',
  });

  const getOrganization = useQuery(ShowOrganization.QUERY, {
    variables: { organizationId: +orgId },
    skip: !orgId,
  });

  const queryForms = useQuery(GqlForms.QUERY, { variables: { organizationId: +orgId } });

  const [setSeasonPaymentAccount] = useMutation(SetSeasonPaymentAccount.MUTATION);
  const [createOnboardingLink, createOnboardingLinkResponse] = useMutation(CreateOnboardingLink.MUTATION);

  const organization = getOrganization.data?.showOrganization;
  const orgForms = queryForms.data?.forms;
  const season = getSeasonData.data?.showSeason;
  const seasonForms = season?.registrationForms;
  const divisionParam = season?.tsDivisionId;

  const savePaymentAccount = async (account) => {
    if (account) {
      await setSeasonPaymentAccount({
        variables: {
          id: +seasonId,
          paymentAccountId: account.id,
        },
      });
      setCurrentDepositAccount(account);
    }
  };

  const handleVerifyOrConnect = async (currentAccount?) => {
    const variables = {
      id: currentAccount?.id ?? currentDepositAccount.id,
      returnUrl: ORG_DEPOSIT_ACCOUNTS_FINANCIALS_URL(organization?.id, +programId, +seasonId),
      refreshUrl: ORG_DEPOSIT_ACCOUNTS_FINANCIALS_URL(organization?.id, +programId, +seasonId),
      isMerchant: true,
      type: 'account_update',
    };

    const response = await createOnboardingLink({ variables });

    if (response) {
      const url = response.data?.createOnboardingLink?.url;

      if (url) {
        setRedirecting(true);
        window.location.href = url;
      }
    }
  };

  /*
    Ideally we wouldn't need to go through org forms annd season forms to find the reg type.
    Update to organization-api to have showSeason return registration type for registrationForms is needed.
  */
  const findRegType = () => {
    const hasReg2Forms = seasonForms.some((form) => {
      const seasonForm = orgForms.find((f) => f.id === form.id);
      return seasonForm.isReg2;
    });
    const hasReg1Forms = seasonForms.some((form) => {
      const seasonForm = orgForms.find((f) => f.id === form.id);
      return !seasonForm.isReg2;
    });

    if (hasReg2Forms && hasReg1Forms) {
      setRegType('both');
    } else if (hasReg2Forms) {
      setRegType('reg2');
    } else {
      setRegType('reg1');
    }
  };

  React.useEffect(() => {
    if (season?.defaultPaymentAccountId && paymentsAccounts?.listPaymentAccounts) {
      setCurrentDepositAccount(
        paymentsAccounts.listPaymentAccounts.find((a) => +a.id === +season.defaultPaymentAccountId) || null
      );
    }
  }, [season, paymentsAccounts]);

  React.useEffect(() => {
    if (seasonForms && orgForms) {
      findRegType();
    }
  }, [seasonForms, orgForms]);

  return (
    <div id="programs" className="container hub-full-width">
      <div className="page-content u-xs-padXl u-flexExpandSides">
        <div className="page-content-header">
          <div className="sui-flex sui-items-center sui-justify-between">
            <h1 data-testid="registration-page-heading">Financials Settings</h1>
            <ButtonGroup>
              <Button testId="SeasonFinancialsPage--settings-button" icon="settings" isActive={true}>
                Financial Settings
              </Button>
              <Button
                testId="SeasonFinancialsPage--reporting-button"
                icon="statistics"
                onClick={() => {
                  window.location.href = `${TEAMSNAP_URL}/${divisionParam}/league_financials/reports/payments`;
                }}
              >
                Financial Reporting
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="page-content-body">
          {(paymentsAccounts?.listPaymentAccounts && regType) && (
            <SeasonMerchantAccount
              organization={organization}
              programId={+programId}
              seasonId={+seasonId}
              accounts={paymentsAccounts.listPaymentAccounts}
              onSave={savePaymentAccount}
              currentAccount={currentDepositAccount}
              onAddAccount={() => setShowDrawer(true)}
              editable={isOneOf(`${orgId}`, context.user, Role.Admin, Role.Owner, Role.OrgAdmin)}
              onVerifyOrConnect={handleVerifyOrConnect}
              disabled={createOnboardingLinkResponse.loading || redirecting}
              regType={regType}
            />
          )}
        </div>
      </div>
      <CreateAccountDrawer
        organization={organization}
        isDrawOpen={showDrawer}
        onDrawClose={() => {
          document.body.classList.remove('scroll-lock');
          setShowDrawer(false);
        }}
        redirectUrl={ORG_DEPOSIT_ACCOUNTS_FINANCIALS_URL(organization?.id, +programId, +seasonId)}
      />
    </div>
  );
};

export default SeasonFinancialsPage;
